// The buy control: one component, three presentations ("a" | "b" | "c", see
// css/buy.css) and two densities ("full" | "compact": a compact control
// shows only the price and the presentation's primary action).
//
// The markup is a real product form (<form action=".../cart/add"> with the
// variant id and quantity), as Shopify themes render it, which the script
// enhances. Two ways in:
//   { formAction, variantId }  the standalone site: the script builds the
//                              form, with its own "Buy now" button
//   { form }                   the store's theme: the section rendered the
//                              form (shopify/sections/nothing-cross.liquid),
//                              so it works before any script runs; "Buy
//                              now", when the section shows it (a setting,
//                              off by default), is Shopify's accelerated
//                              checkout button ({{ form | payment_button }})
// Either way the form holds the same base, which the script then completes:
//   <form class="buy" action=".../cart/add" method="post">
//     <input type="hidden" name="id" ...> <input type="hidden" name="quantity" ...>
//     <span class="buy__actions">
//       <button type="submit" class="buy__add">Add to cart</button>
//       <button type="button" class="buy__now"> | <div class="buy__now">(payment button)</div> | nothing
//     </span>
//   </form>
// Without a Buy now, Add to cart is the only action (buyActions in
// buy-state.js: how each presentation shows that). The submit always goes
// through the connector.
import { BUY_PRESENTATIONS, buyActions, buyView } from "./buy-state.js";

const DENSITIES = ["full", "compact"];

const STATUS_TEXT = {
  loading: "Loading",
  available: "Available",
  adding: "Adding",
  added: "Available",
  soldout: "Sold out",
  error: "Error",
};

// the form's base, as the theme's section renders it too
function newForm({ formAction, variantId, quantity, now }) {
  const form = document.createElement("form");
  form.method = "post";
  form.action = formAction;
  form.className = "buy";
  form.innerHTML = `
    <input type="hidden" name="id" value="${variantId}">
    <input type="hidden" name="quantity" value="${quantity}">
    <span class="buy__actions">
      <button type="submit" class="buy__add">Add to cart</button>${now ? `
      <button type="button" class="buy__now"></button>` : ""}
    </span>`;
  return form;
}

// the parts the script adds around the base
function completeForm(form) {
  const actions = form.querySelector(":scope > .buy__actions");
  actions.insertAdjacentHTML("beforebegin", `
    <span class="buy__state">
      <span class="buy__led" aria-hidden="true"></span>
      <span class="buy__price"></span>
      <span class="buy__status" role="status"></span>
    </span>`);
  actions.insertAdjacentHTML("afterend", `
    <a class="buy__checkout" hidden></a>
    <span class="buy__error" role="alert" hidden>
      <span class="buy__error-text"></span>
      <button type="button" class="buy__retry" hidden>Retry</button>
    </span>`);
}

// a server-rendered form must have the base (its Buy now is optional), and
// not be enhanced twice
function checkServerForm(form, quantity) {
  const fail = (what) => { throw new Error(`buy: the server-rendered form ${what}`); };
  if (!(form instanceof HTMLFormElement)) fail("is not a <form>");
  if (form.dataset.enhanced !== undefined) fail("is already enhanced (one buy control per form)");
  if (!form.querySelector(':scope input[name="id"]')?.value) fail('has no variant id (input name="id")');
  const qty = form.querySelector(':scope input[name="quantity"]');
  if (Number(qty?.value) !== quantity) fail(`has no quantity input with the shop's quantity (${quantity})`);
  for (const part of [":scope > .buy__actions", ":scope > .buy__actions > button.buy__add[type=submit]"]) {
    if (!form.querySelector(part)) fail(`has no ${part.replace(":scope > ", "")}`);
  }
  const now = form.querySelectorAll(".buy__now");
  if (now.length > 1) fail(`has ${now.length} .buy__now`);
  if (now.length === 1 && form.querySelector(":scope > .buy__actions > .buy__now") !== now[0]) fail("has a .buy__now outside .buy__actions");
}

/**
 * shop        from createShop()
 * form        a product form the server rendered (the theme), or else:
 * formAction  the store's cart/add URL (the no-script fallback)
 * variantId   numeric variant id for the form's `id` field
 * now         whether that new form has our own Buy now (false: Add to cart
 *             only, the redesign's pill); a server-rendered form has what
 *             the section rendered
 */
export function createBuyUI({ shop, form: serverForm = null, formAction, variantId, now: withNow = true, presentation = "a", density = "full" }) {
  if (!BUY_PRESENTATIONS.includes(presentation)) throw new Error(`unknown buy presentation "${presentation}"`);
  if (!DENSITIES.includes(density)) throw new Error(`unknown buy density "${density}"`);
  if (serverForm) checkServerForm(serverForm, shop.quantity);
  const form = serverForm ?? newForm({ formAction, variantId, quantity: shop.quantity, now: withNow });
  form.dataset.enhanced = "";
  completeForm(form);
  const $ = (sel) => form.querySelector(sel);
  const add = $(".buy__add");
  // our own button, the store's accelerated checkout (its own label and
  // click), or none (the theme's section with it turned off)
  const now = $(".buy__now");
  const ownNow = now instanceof HTMLButtonElement;
  const checkout = $(".buy__checkout");
  // classes the form came with that aren't ours (Shopify's form tag adds its own)
  const ownClasses = [...form.classList].filter((c) => c !== "buy" && !c.startsWith("buy--"));

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    shop.add();
  });
  if (ownNow) now.addEventListener("click", () => shop.buyNow());
  $(".buy__retry").addEventListener("click", () => shop.load());

  function render(state) {
    const v = buyView(state);
    const actions = buyActions(presentation, v, { withNow: now !== null });
    form.className = [...ownClasses, "buy", `buy--${presentation}`, `buy--${density}`, `buy--primary-${actions.primary}`].join(" ");
    form.dataset.status = v.status;
    form.setAttribute("aria-busy", String(v.status === "loading" || v.status === "adding"));

    $(".buy__price").textContent = v.price;
    $(".buy__status").textContent =
      v.cartCount > 0 && v.status !== "adding" ? `In cart · ${v.cartCount}` : STATUS_TEXT[v.status];

    add.disabled = !v.canBuy;
    add.textContent = actions.addLabel;
    if (ownNow) {
      now.disabled = !v.canBuy;
      now.textContent = actions.nowLabel;
    } else if (now) {
      // the accelerated checkout can't be disabled from outside: take it out
      // of reach (and of the accessibility tree) while nothing can be bought
      now.inert = !v.canBuy;
      now.classList.toggle("is-disabled", !v.canBuy);
    }

    checkout.hidden = !v.checkoutUrl;
    if (v.checkoutUrl) {
      checkout.href = v.checkoutUrl;
      checkout.textContent = `Checkout (${v.cartCount})`;
    }

    $(".buy__error").hidden = !v.message;
    $(".buy__error-text").textContent = v.message;
    $(".buy__retry").hidden = !v.retry;
  }

  const unsubscribe = shop.subscribe(render);
  return {
    el: form,
    setDensity(next) {
      if (!DENSITIES.includes(next)) throw new Error(`unknown buy density "${next}"`);
      density = next;
      render(shop.state);
    },
    destroy() {
      unsubscribe();
      form.remove();
    },
  };
}
